import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../providers/SubscriptionProvider";
import { getDayPosts, generateDateID } from "../services/dayPostService";
import { DayPost } from "../types/post";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import ComingSoon from "../components/ComingSoon";

// Available export variables
const EXPORT_VARIABLES = [
  {
    id: "peakScore",
    label: "Peak Score",
    description: "Your daily readiness score",
  },
  {
    id: "wellnessScore",
    label: "Wellness Score",
    description: "Overall wellness from daily log",
  },
  {
    id: "totalLoad",
    label: "Total Training Load",
    description: "Sum of all training loads for the day",
  },
  {
    id: "restingHR",
    label: "Resting Heart Rate",
    description: "Resting HR from health log",
  },
  {
    id: "hrv",
    label: "HRV",
    description: "Heart rate variability",
  },
  {
    id: "respRate",
    label: "Resp Rate",
    description: "Respiratory rate from health log",
  },
  {
    id: "hlr",
    label: "HLR",
    description: "High Load Ratio (acute:chronic)",
  },
] as const;

type ExportVariableId = (typeof EXPORT_VARIABLES)[number]["id"];

// Helper to format date for input
const formatDateForInput = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

// Helper to parse dateID to readable date
const dateIDToReadable = (dateID: string): string => {
  const year = dateID.slice(0, 4);
  const month = dateID.slice(4, 6);
  const day = dateID.slice(6, 8);
  return `${year}-${month}-${day}`;
};

// Generate array of dateIDs between two dates
const getDateIDsInRange = (startDate: Date, endDate: Date): string[] => {
  const dateIDs: string[] = [];
  const current = new Date(endDate);

  while (current >= startDate) {
    dateIDs.push(generateDateID(current));
    current.setDate(current.getDate() - 1);
  }

  return dateIDs;
};

// Helper to safely format a number value
const formatNumber = (value: unknown, decimals: number): string => {
  if (value === null || value === undefined) return "";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (typeof num !== "number" || isNaN(num)) return "";
  return num.toFixed(decimals);
};

// Extract data from a DayPost based on variable ID
const extractValue = (
  dayPost: DayPost,
  variableId: ExportVariableId,
): string => {
  switch (variableId) {
    case "peakScore":
      return formatNumber(dayPost.peakScoreLog?.peakScore, 1);

    case "wellnessScore":
      // Get the first wellness log's score
      if (dayPost.wellnessLogs && dayPost.wellnessLogs.length > 0) {
        return formatNumber(dayPost.wellnessLogs[0].wellnessScore, 1);
      }
      return "";

    case "totalLoad":
      // Sum all training loads for the day
      if (dayPost.trainingLogs && dayPost.trainingLogs.length > 0) {
        const total = dayPost.trainingLogs.reduce((sum, log) => {
          const load =
            typeof log.load === "string" ? parseFloat(log.load) : log.load;
          return sum + (typeof load === "number" && !isNaN(load) ? load : 0);
        }, 0);
        return total > 0 ? total.toFixed(0) : "";
      }
      return "";

    case "restingHR":
      if (dayPost.healthLogs && dayPost.healthLogs.length > 0) {
        return formatNumber(dayPost.healthLogs[0].rhr, 0);
      }
      return "";

    case "hrv":
      if (dayPost.healthLogs && dayPost.healthLogs.length > 0) {
        return formatNumber(dayPost.healthLogs[0].hrv, 0);
      }
      return "";

    case "respRate":
      // Respiratory rate from health log
      if (dayPost.healthLogs && dayPost.healthLogs.length > 0) {
        return formatNumber(dayPost.healthLogs[0].respRate, 1);
      }
      return "";

    case "hlr":
      return formatNumber(dayPost.peakScoreLog?.hlr, 2);

    default:
      return "";
  }
};

// Generate CSV content
const generateCSV = (
  dayPosts: DayPost[],
  selectedVariables: ExportVariableId[],
): string => {
  // Header row
  const headers = [
    "Date",
    ...selectedVariables.map((id) => {
      const variable = EXPORT_VARIABLES.find((v) => v.id === id);
      return variable?.label || id;
    }),
  ];

  // Data rows (sorted by date, oldest first for CSV)
  const sortedPosts = [...dayPosts].sort((a, b) =>
    a.dateID.localeCompare(b.dateID),
  );

  const rows = sortedPosts.map((dayPost) => {
    const date = dateIDToReadable(dayPost.dateID);
    const values = selectedVariables.map((varId) =>
      extractValue(dayPost, varId),
    );
    return [date, ...values];
  });

  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(","))
    .join("\n");

  return csvContent;
};

// Download CSV file
const downloadCSV = (content: string, filename: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const DownloadData = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isPlusOrHigher } = useSubscription();

  // Premium modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // Date range state (default to last 7 days)
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 6);

  const [startDate, setStartDate] = useState(formatDateForInput(weekAgo));
  const [endDate, setEndDate] = useState(formatDateForInput(today));

  // Selected variables
  const [selectedVariables, setSelectedVariables] = useState<
    Set<ExportVariableId>
  >(new Set<ExportVariableId>(["peakScore", "wellnessScore", "totalLoad"]));

  // Loading/error state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toggle variable selection
  const toggleVariable = (variableId: ExportVariableId) => {
    setSelectedVariables((prev) => {
      const next = new Set(prev);
      if (next.has(variableId)) {
        next.delete(variableId);
      } else {
        next.add(variableId);
      }
      return next;
    });
  };

  // Select/deselect all
  const selectAll = () => {
    const allIds = EXPORT_VARIABLES.map((v) => v.id);
    setSelectedVariables(new Set<ExportVariableId>(allIds));
  };

  const deselectAll = () => {
    setSelectedVariables(new Set<ExportVariableId>());
  };

  // Validate date range
  const validateDateRange = (): string | null => {
    if (!startDate || !endDate) {
      return "Please select both start and end dates";
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      return "Start date must be before end date";
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 365) {
      return "Date range cannot exceed 365 days";
    }

    return null;
  };

  // Handle export
  const handleExport = useCallback(async () => {
    // Check for plus or higher subscription
    if (!isPlusOrHigher) {
      setShowPremiumModal(true);
      return;
    }

    if (!currentUser?.uid) {
      setError("You must be logged in to export data");
      return;
    }

    if (selectedVariables.size === 0) {
      setError("Please select at least one variable to export");
      return;
    }

    const validationError = validateDateRange();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const dateIDs = getDateIDsInRange(start, end);

      console.log(`[Export] Fetching ${dateIDs.length} days of data...`);

      // Fetch all day posts for the date range
      const dayPosts = await getDayPosts(currentUser.uid, dateIDs);

      console.log(`[Export] Received ${dayPosts.length} day posts`);

      // Generate CSV
      const csvContent = generateCSV(
        dayPosts,
        Array.from(selectedVariables) as ExportVariableId[],
      );

      // Create filename with date range
      const filename = `peak_data_${startDate}_to_${endDate}.csv`;

      // Download
      downloadCSV(csvContent, filename);

      console.log(`[Export] Downloaded ${filename}`);
    } catch (err) {
      console.error("[Export] Error:", err);
      setError(err instanceof Error ? err.message : "Failed to export data");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, startDate, endDate, selectedVariables, isPlusOrHigher]);

  // Calculate days in range
  const daysInRange = (() => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return 0;
    return (
      Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );
  })();

  return (
    <div className="w-full pt-16 pb-24 dashboard-background min-h-screen">
      <Header />

      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 p-4">
          <div className="relative">
            <button
              onClick={() => setShowPremiumModal(false)}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm z-10"
            >
              ✕
            </button>
            <ComingSoon featureKey="exportCsv" />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mx-auto max-w-4xl px-4 py-4">
        <div className="flex items-center px-4 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 p-2 hover:bg-base-200 rounded-full transition-colors"
          >
            <ChevronLeftIcon className="h-6 w-6 text-base-content/70" />
          </button>
          <h1 className="text-3xl font-heading font-semibold text-base-content">
            Export Data
          </h1>
        </div>
        <p className="text-base-content/70 font-grotesk text-sm mt-2 px-4">
          Select a date range and variables to export as CSV
        </p>
      </div>

      {/* Gate: Export CSV requires Plus+ */}
      {!isPlusOrHigher && (
        <div className="max-w-4xl mx-auto px-8 pt-4">
          <ComingSoon featureKey="exportCsv" />
        </div>
      )}

      {isPlusOrHigher && <div className="max-w-4xl mx-auto px-8 space-y-6">
        {/* Date Range Selection */}
        <div className="bg-base-100 rounded-xl shadow-sm p-6">
          <div className="flex items-center mb-4">
            <CalendarDaysIcon className="h-5 w-5 text-base-content/60 mr-2" />
            <h2 className="text-lg font-heading font-semibold text-base-content">
              Date Range
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="min-w-0">
              <label className="block text-sm font-medium text-base-content/80 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={formatDateForInput(today)}
                className="w-full min-w-0 px-4 py-3 bg-base-300 border border-base-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base-content cursor-pointer"
                onFocus={(e) => {
                  try {
                    e.target.showPicker?.();
                  } catch (error) {
                    // Fallback for browsers that don't support showPicker
                  }
                }}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-base-content/80 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                max={formatDateForInput(today)}
                className="w-full min-w-0 px-4 py-3 bg-base-300 border border-base-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base-content cursor-pointer"
                onFocus={(e) => {
                  try {
                    e.target.showPicker?.();
                  } catch (error) {
                    // Fallback for browsers that don't support showPicker
                  }
                }}
              />
            </div>
          </div>

          {daysInRange > 0 && (
            <p className="text-sm text-base-content/60 mt-3 font-grotesk">
              {daysInRange} day{daysInRange !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>

        {/* Variable Selection */}
        <div className="bg-base-100 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-base-content">
              Select Variables
            </h2>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
              >
                Select All
              </button>
              <span className="text-base-content/40">|</span>
              <button
                onClick={deselectAll}
                className="text-sm text-base-content/60 hover:text-base-content/80 font-medium"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {EXPORT_VARIABLES.map((variable) => (
              <label
                key={variable.id}
                className={`flex items-center px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                  selectedVariables.has(variable.id)
                    ? "bg-base-200"
                    : "hover:bg-base-200"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedVariables.has(variable.id)}
                  onChange={() => toggleVariable(variable.id)}
                  className="checkbox"
                />
                <span className="ml-3 text-sm text-base-content">
                  {variable.label}
                </span>
              </label>
            ))}
          </div>

          <p className="text-sm text-base-content/60 mt-4 font-grotesk">
            {selectedVariables.size} variable
            {selectedVariables.size !== 1 ? "s" : ""} selected
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Export Button */}
        <button
          onClick={handleExport}
          disabled={loading || selectedVariables.size === 0}
          className={`w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl font-semibold text-lg transition-all ${
            loading || selectedVariables.size === 0
              ? "bg-base-300 text-base-content/60 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl"
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-base-100"></div>
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Export CSV</span>
            </>
          )}
        </button>
      </div>}

      <BottomNavBar />
    </div>
  );
};

export default DownloadData;
